const SHARED_CONTACT_PATTERN =
  /\b(?:shared\s+contact(?:\(s\))?|contatos?\s+compartilhados?)\b/iu;

const NAME_LINE_PATTERN =
  /(?:^|\n)[^\n]*?\b(?:name|nome)\s*:\s*(?:\*\*)?([^\n]+?)(?:\*\*)?\s*(?=\n|$)/iu;

const PHONE_LINE_PATTERN =
  /(?:^|\n)[^\n]*?\b(?:phone|telefone|celular|whatsapp)\s*:\s*(?:\*\*)?([+()\d\s./-]{8,})(?:\*\*)?\s*(?=\n|$)/iu;

const PHONE_CANDIDATE_PATTERN =
  /(^|[^\d])(\+\s*[1-9](?:[\d\s().-]{6,}\d)|\+?\s*55[\s.-]*\(?\d{2}\)?[\s.-]*\d{4,5}[\s.-]*-?[\s.-]*\d{4}|\+?\s*0[\s.-]*\d{2}[\s.-]*\(?\d{2}\)?[\s.-]*\d{4,5}[\s.-]*-?[\s.-]*\d{4}|\+?\s*0[\s.-]*\(?\d{2}\)?[\s.-]*\d{4,5}[\s.-]*-?[\s.-]*\d{4}|\+?\s*\(?\d{2}\)?[\s.-]*\d{4,5}[\s.-]*-?[\s.-]*\d{4})(?![\s./-]*\d)/gu;

const FORMATTED_CPF_PATTERN = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/u;
const FORMATTED_CNPJ_PATTERN = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/u;

const cleanMarkdownValue = value =>
  String(value || '')
    .replace(/\*\*/gu, '')
    .replace(/^[\s>*-]+/u, '')
    .replace(/[\s,;:.]+$/u, '')
    .trim();

const digitsOnly = value => String(value || '').replace(/\D+/gu, '');

const isValidCpf = value => {
  const digits = digitsOnly(value);
  if (digits.length !== 11 || /^(\d)\1{10}$/u.test(digits)) return false;

  const calculateDigit = length => {
    let sum = 0;
    for (let index = 0; index < length; index += 1) {
      sum += Number(digits[index]) * (length + 1 - index);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return (
    calculateDigit(9) === Number(digits[9]) &&
    calculateDigit(10) === Number(digits[10])
  );
};

const isPhoneCandidate = rawValue => {
  const value = cleanMarkdownValue(rawValue);
  const digits = digitsOnly(value);

  if (!digits || FORMATTED_CPF_PATTERN.test(value)) return false;
  if (FORMATTED_CNPJ_PATTERN.test(value)) return false;
  if (!/[()+\s-]/u.test(value) && isValidCpf(digits)) return false;

  if (value.startsWith('+') && !digits.startsWith('55')) {
    return digits.length >= 8 && digits.length <= 15;
  }

  if (digits.startsWith('55') && [12, 13].includes(digits.length)) {
    return true;
  }

  if (digits.startsWith('0')) {
    return [11, 12, 13, 14].includes(digits.length);
  }

  return [10, 11].includes(digits.length);
};

const localBrazilianDigits = value => {
  const digits = digitsOnly(value);

  if (digits.startsWith('55') && [12, 13].includes(digits.length)) {
    return digits.slice(2);
  }

  if (digits.startsWith('0') && [11, 12].includes(digits.length)) {
    return digits.slice(1);
  }

  if (digits.startsWith('0') && [13, 14].includes(digits.length)) {
    return digits.slice(3);
  }

  return digits;
};

export const formatPhoneForDisplay = value => {
  const original = cleanMarkdownValue(value);
  const digits = digitsOnly(original);
  if (original.startsWith('+') && !digits.startsWith('55')) return original;

  const local = localBrazilianDigits(original);

  if (![10, 11].includes(local.length)) return original;

  const ddd = local.slice(0, 2);
  const subscriber = local.slice(2);
  const splitAt = subscriber.length - 4;
  return `+55 (${ddd}) ${subscriber.slice(0, splitAt)}-${subscriber.slice(
    splitAt
  )}`;
};

const phoneAction = ({ phone, name = '' }) => ({
  phone: cleanMarkdownValue(phone),
  name: cleanMarkdownValue(name),
  label: formatPhoneForDisplay(phone),
});

export const extractSharedContact = content => {
  const text = String(content || '');
  if (!SHARED_CONTACT_PATTERN.test(text)) return null;

  const phone = cleanMarkdownValue(text.match(PHONE_LINE_PATTERN)?.[1]);
  if (!isPhoneCandidate(phone)) return null;

  const name =
    cleanMarkdownValue(text.match(NAME_LINE_PATTERN)?.[1]) ||
    'Contato compartilhado';

  return phoneAction({ phone, name });
};

export const extractPhoneActions = (content, { limit = 3 } = {}) => {
  const text = String(content || '');
  const sharedContact = extractSharedContact(text);
  if (sharedContact) {
    return { sharedContact, phones: [] };
  }

  const seen = new Set();

  const matches = Array.from(text.matchAll(PHONE_CANDIDATE_PATTERN));
  const phones = matches.reduce((items, match) => {
    if (items.length >= limit) return items;

    const phone = cleanMarkdownValue(match[2]);
    if (!isPhoneCandidate(phone)) return items;

    const key = digitsOnly(phone);
    if (seen.has(key)) return items;

    seen.add(key);
    return [...items, phoneAction({ phone })];
  }, []);

  return { sharedContact: null, phones };
};
